import React from "react";
import { Scale, FileText, Search, Shield, ArrowRight, BookOpen, Gavel } from "lucide-react";
import { Button } from "@/components/ui/button";

// Use relative URL in production (deployed), or env variable in development
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const Landing = () => {
  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#0B0C10] relative overflow-hidden">
      {/* Background Image with Overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center opacity-20"
        style={{ 
          backgroundImage: "url('https://images.unsplash.com/photo-1625557089494-4989daa75618?crop=entropy&cs=srgb&fm=jpg&q=85')"
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0B0C10]/80 via-[#0B0C10]/90 to-[#0B0C10]" />
      
      {/* Navigation */}
      <nav className="relative z-10 flex items-center justify-between px-8 lg:px-16 py-6">
        <div className="flex items-center gap-3">
          <Scale className="w-8 h-8 text-[#C5A059]" />
          <span className="font-playfair text-2xl font-semibold text-[#E2E8F0] tracking-tight">TALC</span>
        </div>
        <Button 
          onClick={handleLogin}
          data-testid="nav-login-btn"
          className="bg-[#C5A059] text-[#0B0C10] hover:bg-[#D4AF37] font-medium tracking-wide rounded-sm uppercase text-xs px-6 py-3"
        >
          Sign In
        </Button>
      </nav>

      {/* Hero Section */}
      <main className="relative z-10 px-8 lg:px-16 pt-20 lg:pt-32">
        <div className="max-w-5xl">
          <p className="text-[#C5A059] font-mono text-sm tracking-widest uppercase mb-4 animate-fade-in">
            The AI Law Chamber
          </p>
          <h1 className="font-playfair text-5xl lg:text-7xl font-semibold text-[#E2E8F0] leading-tight tracking-tight mb-6 animate-slide-up">
            UK Legal Intelligence,<br />
            <span className="text-[#C5A059]">Reimagined</span>
          </h1>
          <p className="font-manrope text-xl text-[#94A3B8] max-w-2xl mb-10 leading-relaxed animate-slide-up" style={{ animationDelay: '0.1s' }}>
            Your AI-powered legal co-counsel for UK law. Research case law, review contracts, 
            draft documents—all grounded in authoritative UK legal sources.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 animate-slide-up" style={{ animationDelay: '0.2s' }}>
            <Button 
              onClick={handleLogin}
              data-testid="hero-get-started-btn"
              className="bg-[#C5A059] text-[#0B0C10] hover:bg-[#D4AF37] font-medium tracking-wide rounded-sm uppercase text-xs px-8 py-4 h-auto flex items-center gap-2"
            >
              Get Started <ArrowRight className="w-4 h-4" />
            </Button>
            <Button 
              variant="outline"
              data-testid="hero-learn-more-btn"
              className="border border-white/20 text-white hover:bg-white/5 rounded-sm uppercase text-xs px-8 py-4 h-auto"
            >
              Learn More
            </Button>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 mt-24 rounded-sm overflow-hidden">
          <FeatureCard 
            icon={<Search className="w-6 h-6" />}
            title="Legal Research"
            description="Search UK case law and legislation with AI-powered analysis and citations."
          />
          <FeatureCard 
            icon={<FileText className="w-6 h-6" />}
            title="Contract Review"
            description="Analyze contracts governed by English law. Identify risks and suggest amendments."
          />
          <FeatureCard 
            icon={<BookOpen className="w-6 h-6" />}
            title="Document Drafting"
            description="Generate advice notes, letters, and clauses with proper UK legal conventions."
          />
          <FeatureCard 
            icon={<Gavel className="w-6 h-6" />}
            title="Matter Management"
            description="Organize your legal work by matters with full document and chat history."
          />
        </div>

        {/* Trust Indicators */}
        <div className="mt-20 pb-16 border-t border-white/10 pt-12">
          <p className="text-[#64748B] text-sm font-mono uppercase tracking-widest mb-6">Powered By</p>
          <div className="flex flex-wrap items-center gap-8">
            <div className="flex items-center gap-2 text-[#94A3B8]">
              <Shield className="w-5 h-5 text-[#C5A059]" />
              <span className="font-manrope text-sm">UK National Archives Case Law</span>
            </div>
            <div className="flex items-center gap-2 text-[#94A3B8]">
              <BookOpen className="w-5 h-5 text-[#C5A059]" />
              <span className="font-manrope text-sm">Legislation.gov.uk</span>
            </div>
            <div className="flex items-center gap-2 text-[#94A3B8]">
              <Scale className="w-5 h-5 text-[#C5A059]" />
              <span className="font-manrope text-sm">Advanced LLM Reasoning</span>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

const FeatureCard = ({ icon, title, description }) => (
  <div className="bg-[#15171C] p-8 hover:bg-[#1E2028] transition-colors duration-300 group">
    <div className="text-[#C5A059] mb-4 group-hover:scale-110 transition-transform duration-300">
      {icon}
    </div>
    <h3 className="font-playfair text-lg font-medium text-[#E2E8F0] mb-2">{title}</h3>
    <p className="font-manrope text-sm text-[#94A3B8] leading-relaxed">{description}</p>
  </div>
);

export default Landing;
